import React, { useState, useEffect } from 'react';
import './Popup.css';
import supabase from '../../supabaseClient/supabaseClient';
import { roles, countries } from '../Account/dropdowns';
import { useAuth } from '../Login/AuthContext';

export const FIRST_WORKSHEET_KEY = 'hasHadWorksheet';

const Popup = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        acceptedPractices: false,
        name: '',
        role: '',
        school: '',
        country: ''
    });
    const [isTransitioning, setIsTransitioning] = useState(false);
    const { user, profile } = useAuth();

    // Value first: only ask once a teacher has had a worksheet (on a later visit),
    // and never when their profile is already complete.
    useEffect(() => {
        if (!profile || profile.onboardingCompleted) return;
        const hasSeenPopup = localStorage.getItem('hasSeenPopup');
        const hasHadWorksheet = localStorage.getItem(FIRST_WORKSHEET_KEY);
        if (!hasSeenPopup && hasHadWorksheet) {
            setIsOpen(true);
        }
    }, [profile]);

    const handleNext = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setIsTransitioning(false);
        }, 300);
    };

    const handleBack = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentStep(prev => prev - 1);
            setIsTransitioning(false);
        }, 300);
    };

    const handleFinish = async () => {
        try {
            // Save user profile data to Supabase
            const { error } = await supabase
                .from("profiles")
                .update({
                    name: formData.name,
                    role: formData.role,
                    school: formData.school,
                    country: formData.country,
                    onboarding_completed: true
                })
                .eq("id", user.id);

            if (error) {
                console.error("Error saving profile:", error.message);
                // Optionally show an error message to the user
            } else {
                console.log("Profile saved successfully");
            }
        } catch (error) {
            console.error("Unexpected error:", error);
        } finally {
            // Close popup and mark as seen regardless of save success
            setIsOpen(false);
            localStorage.setItem('hasSeenPopup', 'true');
        }
    };
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('overlay')) {
            setIsOpen(false);
            localStorage.setItem('hasSeenPopup', 'true');
        }
    };

    const isStep3Valid = formData.name.trim() !== '' && formData.role.trim() !== '';

    return (
        isOpen && (
            <div className="overlay" onClick={handleOverlayClick}>
                <div className={`popup ${isTransitioning ? 'transitioning' : ''}`}>
                    {/* Step 1: Welcome */}
                    {currentStep === 1 && (
                        <div className="step-content">
                            <h2>Welcome to Worksheep! 🐑</h2>
                            <p>We're thrilled to introduce exciting new features and improvements to help you create custom worksheets in no time!</p>
                            <p><strong>For the best experience, we recommend using a PC.</strong></p>

                            <p>
                                You’ll receive <strong>2 free credits</strong> to get started.<br />
                                Each worksheet download uses <strong>1 credit</strong> <br /> so you can try it out
                                risk-free and only pay as you go.
                            </p>

                            <button
                                className='cont-btn-popup'
                                onClick={handleNext}>
                                Continue
                            </button>
                        </div>
                    )}

                    {/* Step 2: AI Best Practices */}
                    {currentStep === 2 && (
                        <div className="step-content">
                            <h2>AI Best Practices</h2>
                            <p>Before you get started, please review these important guidelines:</p>

                            <ul className="practices-list">
                                <li><strong>Check Accuracy & Bias:</strong> AI isn't perfect—review outputs before using.</li>
                                <li><strong>Use the 80/20 Rule:</strong> Let AI draft 80%, then refine the final 20% yourself.</li>
                                <li><strong>Trust Your Judgment:</strong> Use AI as a starting point, not the final solution, and follow school guidelines.</li>
                                <li><strong>Protect Privacy:</strong> Never include student names or personal info in prompts.</li>
                            </ul>

                            <label className="checkbox-container">
                                <input
                                    type="checkbox"
                                    name="acceptedPractices"
                                    checked={formData.acceptedPractices}
                                    onChange={handleInputChange}
                                />
                                <span>I understand and agree to follow these AI best practices</span>
                            </label>

                            <div className="button-group">
                                <button
                                    className='back-btn-popup'
                                    onClick={handleBack}>
                                    Back
                                </button>
                                <button
                                    className='cont-btn-popup'
                                    onClick={handleNext}
                                    disabled={!formData.acceptedPractices}>
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Profile Details */}
                    {currentStep === 3 && (
                        <div className="step-content">
                            <h2>Tell Us About Yourself</h2>
                            <p>Help us personalize your experience:</p>

                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your name"
                                />
                            </div>

                            <div className="form-group">
                                <label>Role *</label>
                                <select
                                    name="role"
                                    placeholder="Enter your school or organization"
                                    value={formData.role}
                                    onChange={handleInputChange}>
                                    <option value="">Select your role</option>
                                    {roles.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>School/Organization</label>
                                <input
                                    type="text"
                                    name="school"
                                    value={formData.school}
                                    onChange={handleInputChange}
                                    placeholder="Enter your school or organization"
                                />
                            </div>

                            <div className="form-group">
                                <label>Country</label>
                                <select
                                    name="country"
                                    placeholder="Enter your school or organization"
                                    value={formData.country}
                                    onChange={handleInputChange}>
                                    <option value="">Select your country</option>
                                    {countries.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="button-group">
                                <button
                                    className='back-btn-popup'
                                    onClick={handleBack}>
                                    Back
                                </button>
                                <button
                                    className='cont-btn-popup'
                                    onClick={handleNext}
                                    disabled={!isStep3Valid}>
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Final Message */}
                    {currentStep === 4 && (
                        <div className="step-content">
                            <div className="success-icon">✨</div>
                            <h2>You're All Set!</h2>
                            <p>Welcome aboard, {formData.name}! 🎉</p>
                            <p>You're ready to start creating amazing worksheets with Worksheep.</p>
                            <p>If you have any questions or need assistance, feel free to reach out at <a href="mailto:hello@theworksheep.com">hello@theworksheep.com</a></p>

                            <button
                                className='cont-btn-popup'
                                onClick={handleFinish}>
                                Start Creating
                            </button>
                        </div>
                    )}

                    {/* Progress Indicator */}
                    <div className="progress-indicator">
                        {[1, 2, 3, 4].map(step => (
                            <div
                                key={step}
                                className={`progress-dot ${currentStep >= step ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        )
    );
};

export default Popup;